import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormsModule } from '@angular/forms';
import { HelperService } from 'app/shared/services/helper.service';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';

const MAX_NUMBER_OF_COLUMNS = 20;

@Component({
  selector: 'app-dynamic-table-column-settings',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    DragDropModule,
    FormsModule
  ],
  templateUrl: './dynamic-table-column-settings.component.html',
  styleUrl: './dynamic-table-column-settings.component.scss'
})
export class DynamicTableColumnSettingsComponent implements OnInit {
  @Input() columns: any = [];
  @Input() showDatatableColumnSettings: boolean = false;

  @Output() onCloseColumnsSettings = new EventEmitter<void>();
  @Output() onApplyColumnsSettings = new EventEmitter<any>();

  columnsSettings: any = [];
  isVisible = false;
  filteredColumnsSettings: any = []; 
  searchQuery: string = ''; 

  frozenColumns!: any[];

  constructor(
    private helperService: HelperService,
    private translate: TranslateService,
    private toastr: ToastrService,
  ) {}
  
  ngOnInit(): void {
    this.columnsSettings = this.helperService.deepCopy(this.columns).filter((c: any) => !c.frozenLeft);
    this.filteredColumnsSettings = [...this.columnsSettings];
    this.frozenColumns = this.columns.filter((c: any) => c.frozenLeft);    

    this.sortColumnsSettings();

    setTimeout(() => {
      this.isVisible = true;
    });
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.columnsSettings, event.previousIndex, event.currentIndex);
  }

  moveUp(index: number) {
    if (index > 0) {
      const temp = this.columnsSettings[index];
      this.columnsSettings[index] = this.columnsSettings[index - 1];
      this.columnsSettings[index - 1] = temp;
    }
  }

  moveDown(index: number) {
     if (index < this.columnsSettings.length - 1) {
      const temp = this.columnsSettings[index];
      this.columnsSettings[index] = this.columnsSettings[index + 1];
      this.columnsSettings[index + 1] = temp;
    }
  }

  closeColumnsSettings() {
    this.isVisible = false;
    setTimeout(() => {
      this.onCloseColumnsSettings.emit();
    }, 300);
  }

  applyColumnsSettings() {
    let visibleColumns = [];
    const allColumns = this.helperService.deepCopy(this.columnsSettings);
    visibleColumns = allColumns.filter((column: any) => column.showColumn);

    if(visibleColumns.length > MAX_NUMBER_OF_COLUMNS) {
      const errorMessage = this.translate.instant('messages.maximum_columns_table', { max: MAX_NUMBER_OF_COLUMNS, selected:  visibleColumns.length});

      this.toastr.show(
        errorMessage, ToastTypesEnum.WARNING
      );

      return;
    }
    
    this.isVisible = false;

    setTimeout(() => {
      this.onApplyColumnsSettings.emit([...this.frozenColumns, ...this.columnsSettings]);
    }, 300);
  }

  filterColumns() {
    const query = this.searchQuery.trim().toLowerCase(); 
  
    if (!query) {
      this.filteredColumnsSettings = [...this.columnsSettings]; 
      return;
    }
  
    const queryWords = query.split(/\s+/); 
  
    this.filteredColumnsSettings = this.columnsSettings.filter((item: { name: string }) => {
      const translatedColumnName = this.translate.instant(item.name).toLowerCase();
      return queryWords.every(word => translatedColumnName.includes(word));
    });
  }  
  
  sortColumnsSettings() {
    const selected = this.columnsSettings.filter((c: any) => c.showColumn);
    const unselected = this.columnsSettings.filter((c: any) => !c.showColumn)
      .sort((a: any, b: any) => this.translate.instant(a.name).localeCompare(this.translate.instant(b.name)));

    this.columnsSettings = [...selected, ...unselected];
    if (!this.searchQuery) {
      this.filteredColumnsSettings = [...this.columnsSettings];
    }
  }
}
